import { useQuery } from "@apollo/client";
import { Delete } from "@mui/icons-material";
import { Button, MenuItem } from "@mui/material";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryParam, withDefault, NumberParam } from "use-query-params";

import {
  DeleteComplianceInviteMutation,
  DeleteComplianceInviteMutationVariables,
  ListComplianceInvitesQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table, { TableBox } from "~frontend/components/Table";
import Select from "~frontend/components/forms/FormSelect";
import Copy from "~frontend/components/icons/Copy";
import {
  DELETE_COMPLIANCE_INVITE,
  LIST_COMPLIANCE_INVITES,
} from "~frontend/graphql/complianceRegistrationInviteTokens";
import useMutation from "~frontend/util/useMutation";

import { CreateInviteModal } from "./CreateInviteModal";

export const ComplianceInvites = (): ReactElement => {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<ListComplianceInvitesQuery>(
    LIST_COMPLIANCE_INVITES
  );

  const [deleteInvite] = useMutation<
    DeleteComplianceInviteMutation,
    DeleteComplianceInviteMutationVariables
  >(DELETE_COMPLIANCE_INVITE, {
    onErrorSnackbarMessage: "Error deleting compliance registration invite",
    refetchQueries: [LIST_COMPLIANCE_INVITES],
    awaitRefetchQueries: true,
  });

  const [page, setPage] = useQueryParam(
    "invite_page",
    withDefault(NumberParam, 0)
  );
  const [pageSize, setPageSize] = useQueryParam(
    "invite_page_size",
    withDefault(NumberParam, 50)
  );

  const [showCreateInviteModal, setShowCreateInviteModal] = useState(false);

  return (
    <>
      <FlexBox justifyContent="space-between" alignItems="center">
        <h1>Registration Invites</h1>
        <Button
          onClick={() => setShowCreateInviteModal(true)}
          variant="contained"
        >
          + Create Invite
        </Button>
      </FlexBox>
      <CreateInviteModal
        isOpen={showCreateInviteModal}
        onClose={() => setShowCreateInviteModal(false)}
      />
      <TableBox>
        <AsyncWrapper data={data} loading={loading} errors={error}>
          {({ listComplianceInvites }) => (
            <Table<ListComplianceInvitesQuery["listComplianceInvites"][0]>
              columns={[
                {
                  key: "proposedTenantSlug",
                  title: "Proposed Tenant Slug",
                  renderValue: invite => invite.proposedTenantSlug,
                  align: "left",
                },
                {
                  key: "registrationType",
                  title: "Type",
                  renderValue: invite =>
                    invite.registrationType === "TEN_DLC"
                      ? "10DLC"
                      : "Toll-Free",
                },
                {
                  key: "submitted",
                  title: "Submission",
                  renderValue: invite => {
                    if (invite.registrationType === "TOLL_FREE") {
                      return invite.tollFreeRegistrations?.length ? (
                        <Select
                          value=""
                          withNoneOption
                          fullWidth
                          noneOptionLabel="View Submission..."
                        >
                          {invite.tollFreeRegistrations.map(registration => (
                            <MenuItem
                              value={registration.id}
                              key={registration.id}
                              onClick={() =>
                                navigate(
                                  `/staff/compliance-registrations/toll-free/${registration.id}/`
                                )?.catch(console.error)
                              }
                            >
                              {registration.organizationName}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        "--"
                      );
                    }

                    return invite.tenDlcCampaignRegistrations?.length ? (
                      <Select
                        value=""
                        withNoneOption
                        fullWidth
                        noneOptionLabel="View Submission..."
                      >
                        {invite.tenDlcCampaignRegistrations.map(
                          registration => (
                            <MenuItem
                              value={registration.id}
                              key={registration.id}
                              onClick={() =>
                                navigate(
                                  `/staff/compliance-registrations/ten-dlc/${registration.brandRegistration.id}/${registration.id}/`
                                )?.catch(console.error)
                              }
                            >
                              {registration.brandRegistration.organizationName}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    ) : (
                      "--"
                    );
                  },
                },
                {
                  key: "createdAt",
                  title: "Created At",
                  renderValue: invite =>
                    `${DateTime.fromMillis(invite.createdAt).toLocaleString()} by ${invite.createdByStaffEmail}`,
                },
              ]}
              rows={listComplianceInvites}
              emptyState="No invites found"
              totalCount={listComplianceInvites.length ?? 0}
              rowsPerPage={pageSize}
              onChangeRowsPerPage={newPageSize => setPageSize(newPageSize)}
              page={page}
              onChangePage={(page: number) => setPage(page)}
              rowActions={() => [
                {
                  id: "copy-link",
                  type: "button",
                  icon: <Copy />,
                  tooltipLabel: "Copy Invite Link",
                  onClick: async ({
                    token,
                    proposedTenantSlug,
                    registrationType,
                  }) => {
                    await navigator.clipboard.writeText(
                      `${window.location.origin}/compliance/register/${registrationType.toLowerCase().replaceAll("_", "-")}?token=${token}&slug=${proposedTenantSlug}`
                    );
                    enqueueSnackbar("Invite link copied to clipboard");
                  },
                },
                {
                  id: "delete-invite",
                  type: "button",
                  icon: <Delete />,
                  tooltipLabel: "Delete invite",
                  confirm: {
                    title: "Delete Invite",
                    description: "Are you sure you want to delete this invite?",
                  },
                  onClick: async ({ id }) => {
                    await deleteInvite({
                      variables: {
                        input: { id },
                      },
                    });
                  },
                },
              ]}
            />
          )}
        </AsyncWrapper>
      </TableBox>
    </>
  );
};
